"use client";

import { useEffect, useState } from "react";
import { useRouter, useParams } from "next/navigation";
import { useForm, Controller, useWatch } from "react-hook-form";
import { ArrowLeft, Loader2, Plus, Trash2, ImageIcon, Music, Video, FileIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useApiPost } from "@admin/_lib/api-hooks";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { RichHtmlEditor } from "@admin/_components/rich-html-editor";

interface AssetItem { fileservername?: string; baseurl?: string; title?: string; }

interface LessonFormValues {
  lesson_name: string;
  priority: string;
  category_id: string;
  prerequisite_lesson: string;
  accuracy: string;
  description: string;
  image: string;
  audio: string;
  video: string;
  assets: AssetItem[];
  status: boolean;
}

const BASE_URL = process.env.NEXT_PUBLIC_API_CLOUDFRONT || "https://api.divineinfinitebeing.com/";
const CF_BASE = "https://d37pbainmkhx6o.cloudfront.net/";

function resolveUrl(v: unknown): string {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object") {
    const o = v as Record<string, string>;
    if (o.fileservername) return `${CF_BASE}${o.baseurl ?? ""}${o.fileservername}`;
  }
  return "";
}

// Simple select for dynamic options
function DynSelect({ value, onChange, endpoint, valueKey = "_id", labelKey = "name", body = {}, disabled, placeholder }: {
  value: string; onChange: (v: string) => void; endpoint: string;
  valueKey?: string; labelKey?: string; body?: Record<string, unknown>;
  disabled?: boolean; placeholder?: string;
}) {
  const { data: session } = useSession();
  const { data: opts, isLoading } = useQuery({
    queryKey: [endpoint, body],
    queryFn: async () => {
      const res = await fetch(`${BASE_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token}` },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      return (json.results?.res ?? json.results ?? json.dt?.res ?? []) as Record<string, unknown>[];
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    enabled: !!session?.token && (endpoint !== "admin/prerequisite-lesson" || !!(body as any).category_id),
    staleTime: 2 * 60 * 1000,
  });

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || isLoading}
      className="flex h-9 w-full rounded-md border bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 disabled:opacity-50"
      style={{ borderColor: "hsl(240 20% 22%)" }}
    >
      <option value="">{isLoading ? "Loading…" : (placeholder ?? "Select…")}</option>
      {(opts ?? []).map((o) => (
        <option key={String(o[valueKey] ?? o._id)} value={String(o[valueKey] ?? o._id)}>
          {String(o[labelKey] ?? o.lesson_name ?? o.category_name ?? o._id)}
        </option>
      ))}
    </select>
  );
}

// Simple asset list
function AssetList({ items, onChange }: { items: AssetItem[]; onChange: (items: AssetItem[]) => void }) {
  const [newUrl, setNewUrl] = useState("");
  function add() {
    const t = newUrl.trim();
    if (!t) return;
    onChange([...items, { fileservername: t, baseurl: "" }]);
    setNewUrl("");
  }
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2 text-xs p-2 rounded border" style={{ borderColor: "hsl(240 20% 22%)" }}>
          <span className="flex-1 truncate text-muted-foreground">{resolveUrl(item) || item.fileservername || "(no url)"}</span>
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-destructive hover:bg-destructive/10 rounded p-0.5"><Trash2 className="size-3.5" /></button>
        </div>
      ))}
      <div className="flex gap-2">
        <Input placeholder="Asset URL…" value={newUrl} onChange={(e) => setNewUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} className="h-8 text-sm" />
        <Button type="button" variant="outline" size="sm" className="h-8 shrink-0" onClick={add}><Plus className="size-3.5 mr-1" />Add</Button>
      </div>
    </div>
  );
}

export function LessonForm() {
  const router = useRouter();
  const params = useParams();
  const id = params?.id as string | undefined;
  const isEdit = !!id;
  const { data: session } = useSession();

  const { control, handleSubmit, reset, register, formState: { errors } } = useForm<LessonFormValues>({
    defaultValues: { lesson_name: "", priority: "", category_id: "", prerequisite_lesson: "", accuracy: "", description: "", image: "", audio: "", video: "", assets: [], status: true },
  });

  const watchedCategoryId = useWatch({ control, name: "category_id" });

  const { data: record, isLoading: isFetching } = useQuery({
    queryKey: ["admin/lesson-edit", id],
    queryFn: async () => {
      const res = await fetch(`${BASE_URL}admin/lesson-edit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.token}` },
        body: JSON.stringify({ _id: id }),
      });
      const data = await res.json();
      return data.results?.res ?? data.results ?? data;
    },
    enabled: isEdit && !!session?.token,
  });

  useEffect(() => {
    if (!record) return;
    const r = record as Record<string, unknown>;
    function toAssets(v: unknown): AssetItem[] {
      if (!Array.isArray(v)) return [];
      return v.map((x) => typeof x === "string" ? { fileservername: x, baseurl: "" } : (x as AssetItem));
    }
    reset({
      lesson_name: String(r.lesson_name ?? ""),
      priority: String(r.priority ?? ""),
      category_id: String(r.category_id ?? ""),
      prerequisite_lesson: String(r.prerequisite_lesson ?? ""),
      accuracy: String(r.accuracy ?? ""),
      description: String(r.description ?? ""),
      image: resolveUrl(r.image),
      audio: resolveUrl(r.audio),
      video: resolveUrl(r.video),
      assets: toAssets(r.assets),
      status: r.status === 1 || r.status === true,
    });
  }, [record, reset]);

  const addMutation = useApiPost("admin/lesson-add", { invalidateKeys: ["admin/lesson-list", "admin/lesson-list-count"] });
  const editMutation = useApiPost("admin/lesson-update", { invalidateKeys: ["admin/lesson-list", "admin/lesson-list-count"] });
  const mutation = isEdit ? editMutation : addMutation;

  async function onSubmit(values: LessonFormValues) {
    try {
      const body: Record<string, unknown> = {
        lesson_name: values.lesson_name,
        priority: values.priority ? parseFloat(values.priority) : 0,
        category_id: values.category_id,
        prerequisite_lesson: values.prerequisite_lesson || undefined,
        accuracy: values.accuracy ? parseFloat(values.accuracy) : 0,
        description: values.description,
        status: values.status ? 1 : 0,
      };
      if (values.image) body.image = values.image;
      if (values.audio) body.audio = values.audio;
      if (values.video) body.video = values.video;
      if (values.assets.length > 0) body.assets = values.assets;
      if (isEdit) body._id = id;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await mutation.mutateAsync(body as any);
      toast.success(isEdit ? "Lesson updated" : "Lesson created");
      router.push("/admin/training/lessons");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Operation failed");
    }
  }

  if (isEdit && isFetching) {
    return <div className="space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-96 rounded-xl" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => router.back()}><ArrowLeft className="size-4" /></Button>
        <div className="h-6 w-1 rounded-full shrink-0" style={{ background: "linear-gradient(to bottom, #ffa800, #ff7e00)" }} />
        <h1 className="text-xl font-semibold">{isEdit ? "Edit Lesson" : "Add Lesson"}</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
          <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
              Lesson Details
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-5 space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Lesson Name <span className="text-destructive">*</span></Label>
                <Input {...register("lesson_name", { required: "Lesson name is required" })} placeholder="Lesson name" />
                {errors.lesson_name && <p className="text-xs text-destructive">{errors.lesson_name.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Priority</Label>
                <Input type="number" {...register("priority")} placeholder="1" />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Category <span className="text-destructive">*</span></Label>
                <Controller name="category_id" control={control} rules={{ required: "Category is required" }}
                  render={({ field }) => (
                    <DynSelect value={field.value} onChange={field.onChange}
                      endpoint="admin/training-category-fetch" valueKey="_id" labelKey="category_name"
                      placeholder="Select category" />
                  )}
                />
                {errors.category_id && <p className="text-xs text-destructive">{errors.category_id.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Prerequisite Lesson</Label>
                <Controller name="prerequisite_lesson" control={control}
                  render={({ field }) => (
                    <DynSelect value={field.value} onChange={field.onChange}
                      endpoint="admin/prerequisite-lesson"
                      valueKey="_id" labelKey="lesson_name"
                      body={{ category_id: watchedCategoryId }}
                      disabled={!watchedCategoryId}
                      placeholder={watchedCategoryId ? "Select prerequisite" : "Select category first"} />
                  )}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Quiz Accuracy (%) <span className="text-destructive">*</span></Label>
              <Input type="number" {...register("accuracy", { required: "Accuracy is required" })} placeholder="80" className="max-w-xs" />
              {errors.accuracy && <p className="text-xs text-destructive">{errors.accuracy.message}</p>}
            </div>

            <div className="space-y-1.5">
              <Label>Description</Label>
              <Controller name="description" control={control}
                render={({ field }) => <RichHtmlEditor value={field.value} onChange={field.onChange} />}
              />
            </div>

            <Controller name="status" control={control}
              render={({ field }) => (
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <div role="switch" aria-checked={field.value} tabIndex={0}
                    onClick={() => field.onChange(!field.value)}
                    onKeyDown={(e) => e.key === " " && field.onChange(!field.value)}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${field.value ? "bg-orange-500" : "bg-muted"}`}>
                    <span className={`pointer-events-none inline-block size-4 rounded-full bg-white shadow transition-transform ${field.value ? "translate-x-4" : "translate-x-0"}`} />
                  </div>
                  <span className="text-sm">Active</span>
                </label>
              )}
            />
          </CardContent>
        </Card>

        <Card style={{ borderColor: "hsl(240 20% 22%)" }}>
          <CardHeader className="border-b py-3 px-5" style={{ borderColor: "hsl(240 20% 20%)" }}>
            <CardTitle className="flex items-center gap-2 text-base">
              <span className="h-4 w-0.5 rounded-full" style={{ background: "#ffa800" }} />
              Media
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-5 space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5"><ImageIcon className="size-3.5" />Image URL</Label>
                <Input {...register("image")} placeholder="https://… (image URL)" />
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5"><Music className="size-3.5" />Audio URL</Label>
                <Input {...register("audio")} placeholder="https://… (audio URL)" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Video className="size-3.5" />Video URL</Label>
              <Input {...register("video")} placeholder="https://… (video URL)" />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><FileIcon className="size-3.5" />Assets</Label>
              <Controller name="assets" control={control}
                render={({ field }) => <AssetList items={field.value} onChange={field.onChange} />}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <button type="submit" disabled={mutation.isPending}
            className="inline-flex items-center gap-2 rounded-md px-5 py-2 text-sm font-semibold transition-all disabled:opacity-60 btn-orange">
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            {mutation.isPending ? "Saving…" : isEdit ? "Update Lesson" : "Create Lesson"}
          </button>
          <Button type="button" variant="outline" onClick={() => router.back()}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}
